import { vec3 } from 'gl-matrix';
import { DicomImageSlice } from '~/domain/dicom/dicomImageSlice';
import { computeSliceAABB } from './assembleVolume';
import { percentileWindow } from './percentileWindow';

export interface ProjectionResult {
    /** Ready for canvas putImageData */
    imageData: ImageData;
    widthPx: number;
    heightPx: number;
    /** AABB min corner in world space (mm) */
    worldMin: Readonly<vec3>;
    /** AABB max corner in world space (mm) */
    worldMax: Readonly<vec3>;
    /** Scale: mm per output pixel */
    mmPerPixel: number;
}

/**
 * How much of the scout is the maximum along each ray, the rest being the
 * mean.
 *
 * A pure maximum draws two rays alike whenever their brightest voxel is
 * alike, however much material separates them; a pure mean draws them
 * alike whenever the material matches, however dense one of them gets. On
 * CT that reads as bone-only and outline-only respectively. The region is
 * framed against both, so the scout carries both.
 *
 * Each component is windowed on its own percentiles, so nothing here
 * refers to Hounsfield units or to any other scale — see the tests that
 * hold the picture invariant under an affine change of units.
 */
const SCOUT_MAX_WEIGHT = 0.5;

/** A windowed value as 0..1, before the two projections are combined. */
function normalize(value: number, [centre, width]: [number, number]): number {
    if (value === -Infinity) {
        return 0;
    }
    return (value - (centre - width / 2)) / width;
}

/**
 * Auto window/level for a MIP buffer, ignoring its background.
 *
 * The window rule itself is {@link percentileWindow}, shared with the
 * output preview so the two pictures of the same anatomy read alike.
 */
function autoWindowFromBuffer(buffer: Float32Array): [number, number] {
    const values: number[] = [];
    for (let i = 0; i < buffer.length; i++) {
        if (buffer[i] !== -Infinity) {
            values.push(buffer[i]);
        }
    }
    return percentileWindow(values);
}

/**
 * Render a scout projection of raw DICOM slices along one of three
 * orthographic axes.
 *
 * Each slice is visited once, projecting its pixels onto the output plane
 * and accumulating both the maximum and the mean at each output pixel.
 * The two are windowed separately and blended by {@link SCOUT_MAX_WEIGHT}.
 *
 * `maxOutputSize` bounds the longer output dimension, in pixels.
 */
export function renderScoutProjection(
    slices: DicomImageSlice[],
    axis: 'coronal' | 'sagittal' | 'transversal',
    maxOutputSize: number
): ProjectionResult {
    if (slices.length === 0) {
        throw new Error('No slices provided for MIP projection');
    }

    const { worldMin, worldMax } = computeSliceAABB(slices);

    // Compute extent along each world axis
    const extentX = worldMax[0] - worldMin[0];
    const extentY = worldMax[1] - worldMin[1];
    const extentZ = worldMax[2] - worldMin[2];

    // Determine output plane dimensions based on projection axis
    // coronal (AP): projects onto XZ plane
    // sagittal (LAT): projects onto YZ plane
    // transversal (SUP): projects onto XY plane
    let planeW: number;
    let planeH: number;

    switch (axis) {
        case 'coronal':
            planeW = extentX;
            planeH = extentZ;
            break;
        case 'sagittal':
            planeW = extentY;
            planeH = extentZ;
            break;
        case 'transversal':
            planeW = extentX;
            planeH = extentY;
            break;
    }

    // Compute output resolution
    const maxExtent = Math.max(planeW, planeH);
    if (maxExtent <= 0) {
        throw new Error('Invalid AABB extent for projection');
    }
    const mmPerPixel = maxExtent / maxOutputSize;

    const outW = Math.max(1, Math.ceil(planeW / mmPerPixel));
    const outH = Math.max(1, Math.ceil(planeH / mmPerPixel));

    // Accumulation buffer (float, stores max HU values)
    const mipBuffer = new Float32Array(outW * outH);
    mipBuffer.fill(-Infinity);
    // Accumulated in the same pass as the maximum. The traversal is what
    // costs; a second add and a count per splat is not measurable against
    // it, and having both lets the blend happen at display time.
    const sumBuffer = new Float32Array(outW * outH);
    const countBuffer = new Uint32Array(outW * outH);

    // Compute median inter-slice spacing so each voxel can be
    // splatted across its physical footprint. Without splatting,
    // output pixels between slices are never written, producing
    // visible stripes when slice spacing exceeds mmPerPixel.
    //
    // Project each slice's imagePosition onto the slice normal
    // (cross product of row and column directions) so spacing
    // is correct for gantry-tilted and oblique acquisitions.
    let sliceSpacing = mmPerPixel;
    if (slices.length >= 2) {
        const orient0 = slices[0].imageOrientation;
        const row0 = vec3.fromValues(orient0[0], orient0[1], orient0[2]);
        const col0 = vec3.fromValues(orient0[3], orient0[4], orient0[5]);
        const sliceNormal = vec3.create();
        vec3.cross(sliceNormal, row0, col0);
        vec3.normalize(sliceNormal, sliceNormal);

        const projections = slices.map((s) => {
            const ip = s.imagePosition;
            return (
                (ip[0] as number) * sliceNormal[0] +
                (ip[1] as number) * sliceNormal[1] +
                (ip[2] as number) * sliceNormal[2]
            );
        });
        projections.sort((a, b) => a - b);

        const spacings: number[] = [];
        for (let i = 1; i < projections.length; i++) {
            const d = projections[i] - projections[i - 1];
            if (d > 1e-3) {
                spacings.push(d);
            }
        }
        if (spacings.length > 0) {
            spacings.sort((a, b) => a - b);
            sliceSpacing = spacings[Math.floor(spacings.length / 2)];
        }
    }

    // Process each slice
    for (const slice of slices) {
        const pos = slice.imagePosition;
        const orientation = slice.imageOrientation;
        const dx = slice.pixelSpacing[0];
        const dy = slice.pixelSpacing[1];
        const nx = slice.cols;
        const ny = slice.rows;

        // Row and column direction vectors
        const rowDir = vec3.fromValues(
            orientation[0],
            orientation[1],
            orientation[2]
        );
        const colDir = vec3.fromValues(
            orientation[3],
            orientation[4],
            orientation[5]
        );

        // Get pixel data (Int16 for CT/MR)
        let pixelData: Int16Array;
        try {
            pixelData = slice.pixelData;
        } catch {
            // Skip slices where pixel data is not available (e.g. PET)
            continue;
        }

        // Compute voxel footprint half-extents projected onto
        // the output plane (Minkowski sum of in-plane pixel
        // extent and inter-slice extent along the slice normal).
        const normal = vec3.create();
        vec3.cross(normal, rowDir, colDir);

        let halfExtU: number;
        let halfExtV: number;

        switch (axis) {
            case 'coronal':
                halfExtU =
                    (Math.abs(rowDir[0]) * dx +
                        Math.abs(colDir[0]) * dy +
                        Math.abs(normal[0]) * sliceSpacing) /
                    2;
                halfExtV =
                    (Math.abs(rowDir[2]) * dx +
                        Math.abs(colDir[2]) * dy +
                        Math.abs(normal[2]) * sliceSpacing) /
                    2;
                break;
            case 'sagittal':
                halfExtU =
                    (Math.abs(rowDir[1]) * dx +
                        Math.abs(colDir[1]) * dy +
                        Math.abs(normal[1]) * sliceSpacing) /
                    2;
                halfExtV =
                    (Math.abs(rowDir[2]) * dx +
                        Math.abs(colDir[2]) * dy +
                        Math.abs(normal[2]) * sliceSpacing) /
                    2;
                break;
            case 'transversal':
                halfExtU =
                    (Math.abs(rowDir[0]) * dx +
                        Math.abs(colDir[0]) * dy +
                        Math.abs(normal[0]) * sliceSpacing) /
                    2;
                halfExtV =
                    (Math.abs(rowDir[1]) * dx +
                        Math.abs(colDir[1]) * dy +
                        Math.abs(normal[1]) * sliceSpacing) /
                    2;
                break;
        }

        // Convert half-extents to output pixel units
        const splatHalfU = halfExtU / mmPerPixel;
        const splatHalfV = halfExtV / mmPerPixel;

        // For each pixel in the slice, project to output plane
        for (let iy = 0; iy < ny; iy++) {
            // Compute world position for start of row
            const rowStartX = (pos[0] as number) + iy * colDir[0] * dy;
            const rowStartY = (pos[1] as number) + iy * colDir[1] * dy;
            const rowStartZ = (pos[2] as number) + iy * colDir[2] * dy;

            for (let ix = 0; ix < nx; ix++) {
                const wx = rowStartX + ix * rowDir[0] * dx;
                const wy = rowStartY + ix * rowDir[1] * dx;
                const wz = rowStartZ + ix * rowDir[2] * dx;

                const val = pixelData[iy * nx + ix];

                // Map world position to output pixel
                let ox: number;
                let oy: number;

                switch (axis) {
                    case 'coronal':
                        ox = (wx - worldMin[0]) / mmPerPixel;
                        // Z increases downward in output
                        oy = (worldMax[2] - wz) / mmPerPixel;
                        break;
                    case 'sagittal':
                        ox = (wy - worldMin[1]) / mmPerPixel;
                        oy = (worldMax[2] - wz) / mmPerPixel;
                        break;
                    case 'transversal':
                        ox = (wx - worldMin[0]) / mmPerPixel;
                        oy = (wy - worldMin[1]) / mmPerPixel;
                        break;
                }

                // Splat voxel to all output pixels within its
                // physical footprint
                const xLo = Math.max(0, Math.floor(ox - splatHalfU));
                const xHi = Math.min(outW - 1, Math.floor(ox + splatHalfU));
                const yLo = Math.max(0, Math.floor(oy - splatHalfV));
                const yHi = Math.min(outH - 1, Math.floor(oy + splatHalfV));

                for (let sy = yLo; sy <= yHi; sy++) {
                    const rowBase = sy * outW;
                    for (let sx = xLo; sx <= xHi; sx++) {
                        const idx = rowBase + sx;
                        if (val > mipBuffer[idx]) {
                            mipBuffer[idx] = val;
                        }
                        sumBuffer[idx] += val;
                        countBuffer[idx]++;
                    }
                }
            }
        }
    }

    // The mean along each ray, over the voxels that actually reached this
    // pixel — the splat footprint varies, so the divisor is counted rather
    // than assumed from the slice count.
    const meanBuffer = new Float32Array(outW * outH);
    for (let i = 0; i < meanBuffer.length; i++) {
        meanBuffer[i] =
            countBuffer[i] > 0 ? sumBuffer[i] / countBuffer[i] : -Infinity;
    }

    // Each projection is windowed on its OWN distribution before they are
    // combined. Blending the two in Hounsfield units instead lets the
    // maximum dominate: along a CT ray it varies over roughly the bone-to-
    // air range while the mean varies over a fraction of that, so a raw
    // weighted sum is the maximum with less contrast, and the weight would
    // have to be retuned per body site.
    const maxWindow = autoWindowFromBuffer(mipBuffer);
    const meanWindow = autoWindowFromBuffer(meanBuffer);

    const imageData = new ImageData(outW, outH);
    const data = imageData.data;

    for (let i = 0; i < mipBuffer.length; i++) {
        const byte =
            countBuffer[i] === 0
                ? 0
                : Math.round(
                      255 *
                          Math.min(
                              1,
                              Math.max(
                                  0,
                                  SCOUT_MAX_WEIGHT *
                                      normalize(mipBuffer[i], maxWindow) +
                                      (1 - SCOUT_MAX_WEIGHT) *
                                          normalize(meanBuffer[i], meanWindow)
                              )
                          )
                  );

        const o = i * 4;
        data[o] = byte;
        data[o + 1] = byte;
        data[o + 2] = byte;
        data[o + 3] = 255;
    }

    return {
        imageData,
        widthPx: outW,
        heightPx: outH,
        worldMin: vec3.clone(worldMin),
        worldMax: vec3.clone(worldMax),
        mmPerPixel,
    };
}
